// Camera-related type definitions

import type { Quaternion } from '@/utils/quaternion'

// Legacy Euler-based camera state
export interface CameraStateEuler {
  position: {
    x: number
    y: number
    z: number
  }
  rotation: {
    rx: number  // pitch in radians
    ry: number  // yaw in radians
    rz: number  // roll in radians
  }
}

// New quaternion-based camera state
export interface CameraStateQuaternion {
  position: {
    x: number
    y: number
    z: number
  }
  quaternion: Quaternion
}

// Union type that supports both formats
export type CameraState = CameraStateEuler | CameraStateQuaternion

// Type guard to check if camera state uses quaternion
export function isQuaternionCamera(camera: CameraState): camera is CameraStateQuaternion {
  return 'quaternion' in camera
}

// Type guard to check if camera state uses Euler angles
export function isEulerCamera(camera: CameraState): camera is CameraStateEuler {
  return 'rotation' in camera
}

export interface AnimationKeyframe {
  id: string
  time: number  // in seconds
  camera: CameraState
}

export interface ShowcaseAnimation {
  keyframes: AnimationKeyframe[]
  duration: number  // total duration in seconds
  loop: boolean
}

export interface ModelTransform {
  position: {
    x: number
    y: number
    z: number
  }
  rotation: {
    rx: number  // pitch in radians
    ry: number  // yaw in radians
    rz: number  // roll in radians
  }
  scale: {
    x: number
    y: number
    z: number
  }
}

export interface ModelMetadata {
  defaultCamera?: CameraState
  showcaseAnimation?: ShowcaseAnimation
  modelTransform?: ModelTransform
  // Add other metadata fields as needed
}

// Utility type for camera props
export interface CameraAnimationProps {
  keyframes: AnimationKeyframe[]
  duration: number
  loop?: boolean
  onAnimationComplete?: () => void
}